import { Injectable } from '@angular/core';
import { ReportingApiService } from '@epicuro-next/data/reporting-api';
import { UsersApiService } from '@epicuro-next/data/users';
import { selectUser } from '@epicuro-next/state/user-state';
import { isNonNullish } from '@epicuro-next/utilities';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { EMPTY } from 'rxjs';
import {
  filter,
  map,
  mergeMap,
  shareReplay,
  switchMap,
  take,
  withLatestFrom,
} from 'rxjs/operators';

import {
  AnalyticsSignalREvent,
  IListOpenedSessionReportEvent,
} from '../../models/signalr-events';
import {
  addLiveUsersToListEntity,
  loadListLiveViewers,
  reportDataReceived,
  upsertAnalyticsEntities,
} from '../analytics-entities.actions';
import { AnalyticsEntity, IAnalyticsUserInfo } from '../interfaces';
import {
  selectCanSeeLiveViewers,
  selectListAnalyticsEntity,
} from '../selectors';
import { entityToLiveViewers } from '../utils/live-viewers';

@Injectable()
export class AnalyticsLiveViewersEffects {
  private cansSeeLiveViewers$ = this.store.pipe(
    select(selectCanSeeLiveViewers),
    shareReplay({ bufferSize: 1, refCount: true }),
    filter(isNonNullish),
  );

  private user$ = this.store.pipe(
    select(selectUser),
    shareReplay({ bufferSize: 1, refCount: true }),
    filter(isNonNullish),
  );

  constructor(
    private actions$: Actions,
    private store: Store,
    private usersApiService: UsersApiService,
    private reportingApi: ReportingApiService,
  ) {}

  public fetchListLiveViewers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadListLiveViewers),
      withLatestFrom(this.cansSeeLiveViewers$, this.user$),
      filter(([, canSee]) => canSee),
      switchMap(([action, , currentUser]) =>
        this.reportingApi.getProductListLiveViewers(action.listId).pipe(
          map((users) =>
            addLiveUsersToListEntity({
              usersPayload: {
                productListId: action.listId,
                users: users
                  .filter((user) => user.userId !== currentUser.externalId)
                  .map((user) => ({
                    externalId: user.userId,
                    internalId: undefined,
                  })),
              },
            }),
          ),
        ),
      ),
    ),
  );

  public liveUsersSignalREvent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(reportDataReceived),
      filter(
        (action) =>
          action.event.eventName ===
          AnalyticsSignalREvent.ListOpenedSessionReportEvent,
      ),
      map(({ event }) =>
        addLiveUsersToListEntity({
          usersPayload: (event as IListOpenedSessionReportEvent).payload,
        }),
      ),
    ),
  );

  public fetchLiveUsersData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addLiveUsersToListEntity),
      mergeMap(({ usersPayload }) =>
        this.store.pipe(
          select(selectListAnalyticsEntity, usersPayload.productListId),
          map((entity) =>
            entity ? { entity, users: usersPayload.users } : undefined,
          ),
          take(1),
        ),
      ),
      filter(isNonNullish),
      switchMap(({ entity, users }) => {
        const entityUsers = entityToLiveViewers(entity);
        const usersNeedApiData = users.filter(
          (user) =>
            !entityUsers.find(
              (u) =>
                u.externalId === user.externalId && isNonNullish(u.userInfo),
            ),
        );

        if (usersNeedApiData.length === 0) return EMPTY;

        return this.usersApiService
          .getUsersByExternalId(usersNeedApiData.map((u) => u.externalId))
          .pipe(
            map((result) => {
              const usersData: { [key: string]: IAnalyticsUserInfo } = {};
              result.forEach((user) => (usersData[user.userId] = user));
              const updatedEntity: AnalyticsEntity = {
                ...entity,
                liveViewingUsers: usersData,
              };

              return upsertAnalyticsEntities({ entities: [updatedEntity] });
            }),
          );
      }),
    ),
  );
}
